/**
 * main.js
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Plugins
import { registerPlugins } from '@/plugins'

// Components
import App from './App.vue'

// Composables
import { createApp } from 'vue'
import { createPinia } from 'pinia' // Import Pinia

// Routing
import router from '@/router'
import { installVueQuery } from '@/plugins/vueQuery'

// Error Tracking - Sentry
import * as Sentry from '@sentry/vue'
import { loadHubSpot, redactSentryEvent, replayPercentFor } from '@/utils/publicReportRoute'

// Analytics - Mixpanel
import analytics from '@/services/analytics'

// Design System - CSS Variables
import '@/design-system/styles/theme-variables.css'

// Initialize theme
import { useTheme } from '@/design-system/composables/useTheme'
const { applyTheme } = useTheme()
const savedTheme = localStorage.getItem('synapsee-theme') || 'synapsee'
applyTheme(savedTheme)

// Import Fonts - Geist
import "@fontsource/geist-sans/300.css"; // Light
import "@fontsource/geist-sans/400.css"; // Regular
import "@fontsource/geist-sans/500.css"; // Medium
import "@fontsource/geist-sans/600.css"; // SemiBold
import "@fontsource/geist-sans/700.css"; // Bold
import "@fontsource/geist-mono/400.css"; // Mono Regular
import "@fontsource/geist-mono/500.css"; // Mono Medium


// App creation
const app = createApp(App)
const pinia = createPinia() // Create Pinia instance

// Initialize Sentry for error tracking
Sentry.init({
  app,
  dsn: 'https://1cb48373af91da814d4c920ab1c90168@o4511026449743872.ingest.us.sentry.io/4511224947146752',
  environment: import.meta.env.MODE,
  // SYN-1681: keep PII out of Sentry (aligned with backend observability.py).
  // No IP address, no request headers/cookies — errors are triaged by stack
  // trace + release, not by user identity.
  sendDefaultPii: false,
  attachStacktrace: true,
  // Backstop: filter user-flow auth/permission AxiosErrors that escape the
  // axios interceptor's allow-list (401/402/403). The interceptor already
  // skips them but components that re-throw or capture manually can leak
  // them in — these are user-policy, not bugs, and were flooding Sentry
  // (TJ-2Q/2M/2K/2J/2H/2R on /superadmin).
  ignoreErrors: [
    /AxiosError.*status code 401/i,
    /AxiosError.*status code 402/i,
    /AxiosError.*status code 403/i,
    /Request failed with status code 401/i,
    /Request failed with status code 402/i,
    /Request failed with status code 403/i,
  ],
  beforeSend(event, hint) {
    const err = hint?.originalException;
    const status = err?.response?.status;
    if (status === 401 || status === 402 || status === 403) {
      return null;
    }
    // SYN-2721: o token do relatório público (`/r/<token>`) é credencial.
    return redactSentryEvent(event);
  },
})

// Initialize Mixpanel analytics
// SYN-2721: Replay desligado e HubSpot fora na página pública — os dois gravam a
// URL com o token do link.
analytics.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
  replayPercent: replayPercentFor(import.meta.env.VITE_MIXPANEL_REPLAY_PERCENT),
})
loadHubSpot()

registerPlugins(app)
app.use(pinia) // Use Pinia
app.use(router)
installVueQuery(app)
app.mount('#app')
